import React from 'react';

/**
 * Cockpit Card — solid surface, hairline border, 14px radius.
 * Elevation is earned on hover when `interactive`.
 */
export interface CardProps {
  interactive?: boolean;
  padding?: string;
  title?: React.ReactNode;
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  className?: string;
  style?: React.CSSProperties;
}

export default function Card({
  interactive = false,
  padding = 'var(--space-5)',
  title,
  eyebrow,
  actions,
  children,
  onClick,
  className = '',
  style,
}: CardProps) {
  return (
    <div
      onClick={onClick}
      className={`ck-card ${interactive ? 'hover-lift' : ''} ${className}`}
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-rest)',
        padding,
        cursor: interactive ? 'pointer' : 'default',
        ...style,
      }}
    >
      {eyebrow || title || actions ? (
        <div
          className="ck-card-head"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
          }}
        >
          {/* .ck-card-head wraps on phones: the actions drop under the title instead of squeezing it */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            {eyebrow ? <span className="t-eyebrow">{eyebrow}</span> : null}
            {title ? (
              <span
                style={{
                  fontSize: 'var(--text-md)',
                  fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                  letterSpacing: 'var(--tracking-tight)',
                  color: 'var(--text-body)',
                }}
              >
                {title}
              </span>
            ) : null}
          </div>
          {actions ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginLeft: 'auto', maxWidth: '100%' }}>{actions}</div>
          ) : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}
